// Import React hooks for context management and state
import React, { createContext, useContext, useState, useEffect } from 'react';

// Import our Supabase client instance
import { supabase } from '../lib/supabase';

// Create the authentication context
const AuthContext = createContext({});

// Export the AuthProvider component that will wrap the entire application
export const AuthProvider = ({ children }) => {
  // State to hold the current session (null when logged out)
  const [session, setSession] = useState(null);
  
  // State to track loading status during initial session check
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for an existing session when the app loads
    const initializeAuth = async () => {
      try {
        // Get the current session from Supabase
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error fetching session:', error.message);
        } else {
          // Set the session if one exists
          setSession(currentSession);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        // Mark loading as complete
        setLoading(false);
      }
    };

    initializeAuth();

    // Set up a real-time listener for auth state changes
    // This is critical for handling sign-ins, sign-outs, and token refreshes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        // Log auth events for debugging (can be removed in production)
        console.log('Auth event:', event);
        
        // Update the session state whenever it changes
        setSession(currentSession);
        
        // Handle specific auth events if needed
        switch (event) {
          case 'SIGNED_IN':
            console.log('User signed in');
            break;
          case 'SIGNED_OUT':
            console.log('User signed out');
            break;
          case 'TOKEN_REFRESHED':
            console.log('Token refreshed');
            break;
          case 'USER_UPDATED':
            console.log('User profile updated');
            break;
          default:
            break;
        }
      }
    );

    // Cleanup function to unsubscribe from the auth listener
    return () => {
      subscription?.unsubscribe();
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  // Auth helper functions that components can use

  // Sign up a new user with email and password
  const signUp = async (email, password, additionalData = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: additionalData // Optional user metadata
      }
    });
    return { data, error };
  };

  // Sign in an existing user with email and password
  const signInWithPassword = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { data, error };
  };

  // Sign out the current user
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error.message);
    }
    return { error };
  };

  // Send a password reset email
  const resetPassword = async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });
    return { data, error };
  };

  // Update the current user's profile
  const updateProfile = async (updates) => {
    const { data, error } = await supabase.auth.updateUser(updates);
    return { data, error };
  };

  // The value object that will be available to all child components
  const value = {
    // State
    session,
    user: session?.user ?? null, // Convenient access to the user object
    loading,
    
    // Auth methods
    signUp,
    signInWithPassword,
    signOut,
    resetPassword,
    updateProfile,
    
    // Direct access to supabase auth for advanced use cases
    supabaseAuth: supabase.auth
  };

  // Provide the auth context to all child components
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to easily access the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  // Throw an error if the hook is used outside of an AuthProvider
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

// Export the context itself for advanced use cases
export default AuthContext;